(() => {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const STORAGE_ZIP = 'anx_zip';
  const STORAGE_DEST = 'anx_dest_full';
  const STORAGE_PLAN_MODE = 'aonsk_seed_finder_plan_mode';
  const STORAGE_PLAN_DATE = 'aonsk_seed_finder_plan_date';

  const normalizeText = (value) => {
    if (Array.isArray(value)) return value.join(' ').toLowerCase();
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return Object.values(value).join(' ').toLowerCase();
    return String(value).toLowerCase();
  };

  const numberFrom = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const match = String(value).match(/\d+/);
    return match ? Number(match[0]) : null;
  };

  const parseRange = (label) => {
    const numbers = String(label || '').match(/\d+/g) || [];
    if (!numbers.length) return { min: null, max: null };
    if (numbers.length === 1) {
      const value = Number(numbers[0]);
      return { min: value, max: /\+|over|more/i.test(label) ? 9999 : value };
    }
    return { min: Number(numbers[0]), max: Number(numbers[1]) };
  };

  const hashNumber = (value) => {
    let hash = 0;
    const input = String(value || '');
    for (let index = 0; index < input.length; index += 1) {
      hash = ((hash << 5) - hash) + input.charCodeAt(index);
      hash |= 0;
    }
    return Math.abs(hash);
  };

  const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + Number(days || 0));
    return result;
  };

  const daysBetween = (from, to) => Math.round((startOfDay(to) - startOfDay(from)) / DAY_MS);

  const formatDate = (date, options = {}) => date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: options.year ? 'numeric' : undefined
  });

  const dateFromInput = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const toInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const dateFromMonthDay = (monthDay, year) => {
    const parts = String(monthDay || '').split('-').map(Number);
    if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
    return new Date(year, parts[0] - 1, parts[1]);
  };

  const buildFrostTable = () => {
    const table = {};
    const fill = (start, end, last, first) => {
      for (let prefix = start; prefix <= end; prefix += 1) {
        table[String(prefix).padStart(3, '0')] = { last, first };
      }
    };

    fill(6, 9, '01-01', '12-31');
    fill(967, 968, '01-01', '12-31');
    fill(10, 27, '05-15', '10-10');
    fill(28, 29, '05-10', '10-15');
    fill(30, 38, '05-20', '10-05');
    fill(39, 49, '05-25', '10-01');
    fill(50, 59, '05-20', '10-05');
    fill(60, 69, '04-25', '10-25');
    fill(70, 89, '04-20', '10-25');
    fill(100, 119, '04-20', '10-25');
    fill(120, 149, '05-10', '10-10');
    fill(150, 169, '04-30', '10-20');
    fill(170, 196, '04-25', '10-25');
    fill(197, 199, '04-10', '11-01');
    fill(200, 205, '04-05', '11-05');
    fill(206, 219, '04-05', '10-31');
    fill(220, 246, '04-10', '10-31');
    fill(247, 268, '04-30', '10-20');
    fill(270, 289, '04-05', '11-15');
    fill(290, 299, '03-30', '11-20');
    fill(300, 319, '03-25', '11-18');
    fill(398, 399, '03-25', '11-18');
    fill(320, 339, '02-15', '12-10');
    fill(340, 349, '02-01', '12-20');
    fill(350, 369, '03-25', '11-10');
    fill(386, 397, '03-25', '11-10');
    fill(370, 385, '04-15', '10-31');
    fill(400, 427, '04-20', '10-20');
    fill(430, 459, '05-10', '10-15');
    fill(460, 479, '05-10', '10-10');
    fill(480, 497, '05-20', '10-05');
    fill(498, 499, '06-01', '09-20');
    fill(500, 529, '05-05', '10-05');
    fill(530, 549, '05-15', '10-05');
    fill(550, 567, '05-20', '09-30');
    fill(600, 629, '04-25', '10-20');
    fill(630, 658, '04-20', '10-20');
    fill(570, 577, '05-15', '09-30');
    fill(580, 588, '05-25', '09-20');
    fill(660, 679, '04-20', '10-20');
    fill(680, 693, '05-10', '10-05');
    fill(700, 714, '02-25', '11-20');
    fill(716, 729, '03-30', '11-05');
    fill(730, 749, '04-10', '10-30');
    fill(750, 769, '03-20', '11-10');
    fill(770, 775, '02-25', '11-25');
    fill(776, 799, '03-15', '11-15');
    fill(870, 884, '04-15', '10-25');
    fill(850, 853, '02-20', '12-05');
    fill(854, 858, '04-10', '11-15');
    fill(859, 865, '05-20', '10-01');
    fill(800, 816, '05-20', '09-30');
    fill(820, 831, '06-01', '09-15');
    fill(590, 599, '05-30', '09-20');
    fill(832, 838, '05-15', '10-05');
    fill(840, 847, '05-10', '10-10');
    fill(889, 891, '02-20', '12-10');
    fill(894, 898, '05-10', '10-05');
    table['892'] = { last: '04-20', first: '10-10' };
    table['893'] = { last: '04-20', first: '10-10' };
    fill(900, 916, '02-20', '12-10');
    fill(917, 919, '03-05', '11-30');
    fill(920, 921, '02-10', '12-15');
    table['922'] = { last: '02-01', first: '12-20' };
    fill(923, 925, '03-10', '11-25');
    fill(926, 928, '02-15', '12-10');
    fill(930, 935, '03-01', '11-25');
    fill(936, 939, '03-10', '11-25');
    fill(940, 949, '03-15', '11-20');
    fill(950, 959, '03-15', '11-10');
    fill(960, 961, '04-25', '10-20');
    fill(970, 973, '04-20', '10-25');
    fill(974, 976, '04-25', '10-20');
    fill(977, 979, '05-05', '10-10');
    fill(980, 986, '04-20', '10-25');
    fill(988, 989, '05-05', '10-10');
    fill(990, 994, '05-05', '10-10');
    fill(995, 999, '06-05', '08-30');
    table['27924'] = { last: '04-05', first: '11-15' };
    table['27983'] = { last: '04-05', first: '11-15' };
    return table;
  };

  const FROST = buildFrostTable();

  class AonskSeedFinder extends HTMLElement {
    constructor() {
      super();
      this.config = {};
      this.steps = [];
      this.currentStep = 0;
    }

    connectedCallback() {
      if (this.dataset.finderInitialized === 'true') return;
      this.dataset.finderInitialized = 'true';

      const configNode = this.querySelector('[data-finder-config]');
      if (!configNode) return;

      try {
        this.config = JSON.parse(configNode.textContent);
      } catch (error) {
        console.warn('Guided seed finder configuration could not be read.', error);
        return;
      }

      this.dialog = this.querySelector('[data-finder-dialog]');
      this.results = this.querySelector('[data-finder-results]');
      this.resultGrid = this.querySelector('[data-finder-result-grid]');
      this.laterGrid = this.querySelector('[data-finder-later-grid]');
      this.laterWrap = this.querySelector('[data-finder-later-wrap]');
      this.resultsSummary = this.querySelector('[data-finder-results-summary]');
      this.resultsHeading = this.querySelector('[data-finder-results-heading]');
      this.seasonResult = this.querySelector('[data-finder-season-result]');
      this.browseLink = this.querySelector('[data-finder-browse]');
      this.note = this.querySelector('[data-finder-note]');
      this.noteForm = this.querySelector('[data-finder-note-form]');
      this.progressBar = this.querySelector('[data-finder-progress-bar]');
      this.stepCount = this.querySelector('[data-finder-step-count]');
      this.backButton = this.querySelector('[data-finder-back]');
      this.skipButton = this.querySelector('[data-finder-skip]');
      this.nextButton = this.querySelector('[data-finder-next]');
      this.finishButton = this.querySelector('[data-finder-finish]');
      this.zipInput = this.querySelector('[data-finder-zip]');
      this.planSelect = this.querySelector('[data-finder-plan-select]');
      this.dateInput = this.querySelector('[data-finder-date]');
      this.dateWrap = this.querySelector('[data-finder-date-wrap]');
      this.todayLabel = this.querySelector('[data-finder-today]');
      this.seasonPreview = this.querySelector('[data-finder-season-preview]');
      this.seasonError = this.querySelector('[data-finder-season-error]');
      this.dialogContext = this.querySelector('[data-finder-dialog-context]');
      this.editContextButton = this.querySelector('[data-finder-edit-context]');
      this.steps = Array.from(this.dialog?.querySelectorAll('[data-finder-step]') || []).filter((step) => !step.hidden);

      this.loadSavedContext();
      this.bindEvents();
      this.syncChoiceStates();
      this.updateDateVisibility();
      this.updateSeasonPreview();
      this.updateStep();
    }

    bindEvents() {
      this.querySelectorAll('[data-finder-open]').forEach((button) => {
        button.addEventListener('click', () => this.prepareAndOpen());
      });

      this.querySelectorAll('[data-finder-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.querySelectorAll('[data-finder-show-all]').forEach((button) => {
        button.addEventListener('click', () => this.scrollToProducts());
      });

      this.noteForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.prepareAndOpen();
      });

      this.querySelectorAll('[data-finder-quick-match]').forEach((button) => {
        button.addEventListener('click', () => {
          this.applyNaturalNote(this.combinedNaturalNote());
          if (!this.validateSeasonStep()) {
            this.focusPlanning();
            return;
          }
          this.finish();
        });
      });

      this.querySelectorAll('[data-finder-note-example]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!this.note) return;
          this.note.value = button.dataset.finderNoteExample || '';
          this.note.focus();
        });
      });

      this.querySelectorAll('[data-finder-quick]').forEach((button) => {
        button.addEventListener('click', () => {
          const selected = button.getAttribute('aria-pressed') === 'true';
          button.setAttribute('aria-pressed', selected ? 'false' : 'true');
          button.classList.toggle('is-selected', !selected);
        });
      });

      this.editContextButton?.addEventListener('click', () => this.focusPlanning());

      this.querySelectorAll('[data-finder-edit]').forEach((button) => {
        button.addEventListener('click', () => {
          this.updateDialogContext();
          this.open();
        });
      });

      this.querySelectorAll('[data-finder-reset]').forEach((button) => {
        button.addEventListener('click', () => this.reset());
      });

      this.backButton?.addEventListener('click', () => {
        this.currentStep = Math.max(0, this.currentStep - 1);
        this.updateStep();
      });

      this.skipButton?.addEventListener('click', () => this.skipCurrent());
      this.nextButton?.addEventListener('click', () => this.advance());
      this.finishButton?.addEventListener('click', () => this.finish());

      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      this.querySelectorAll('.aonsk-finder__choice input').forEach((input) => {
        input.addEventListener('change', () => {
          const choice = input.closest('.aonsk-finder__choice');
          if (choice) {
            choice.closest('.aonsk-finder__step')?.querySelectorAll('.aonsk-finder__choice').forEach((item) => {
              item.classList.toggle('is-selected', Boolean(item.querySelector('input')?.checked));
            });
          }
          if (input.dataset.planMode) {
            this.updateDateVisibility();
            this.savePlanningContext();
            this.updateSeasonPreview();
          }
        });
      });

      this.zipInput?.addEventListener('input', () => {
        this.zipInput.value = this.zipInput.value.replace(/\D/g, '').slice(0, 5);
        this.clearSeasonError();
        this.updateSeasonPreview();
        this.updateDialogContext();
      });

      this.zipInput?.addEventListener('change', () => {
        this.savePlanningContext();
        this.updateSeasonPreview();
        this.updateDialogContext();
      });

      this.planSelect?.addEventListener('change', () => {
        this.clearSeasonError();
        this.updateDateVisibility();
        this.savePlanningContext();
        this.updateSeasonPreview();
        this.updateDialogContext();
      });

      this.dateInput?.addEventListener('change', () => {
        this.clearSeasonError();
        this.savePlanningContext();
        this.updateSeasonPreview();
        this.updateDialogContext();
      });
    }

    loadSavedContext() {
      if (this.todayLabel) {
        this.todayLabel.textContent = `Today is ${formatDate(new Date(), { year: true })}.`;
      }

      let savedZip = '';
      let savedMode = 'now';
      let savedDate = '';
      const sharedContext = window.AONSK_GARDEN_DATA?.readProfilePreferences?.('seedFinder') || {};
      const sharedClimate = window.AONSK_GARDEN_DATA?.readClimateProfile?.();
      try {
        savedZip = sharedContext.zip || sharedClimate?.zip || localStorage.getItem(STORAGE_ZIP) || '';
        savedMode = sharedContext.mode || localStorage.getItem(STORAGE_PLAN_MODE) || 'now';
        savedDate = sharedContext.planDate || localStorage.getItem(STORAGE_PLAN_DATE) || '';
      } catch (error) {
        // The guide still works when browser storage is unavailable.
      }

      if (this.zipInput && /^\d{5}$/.test(savedZip)) this.zipInput.value = savedZip;
      if (this.planSelect) this.planSelect.value = savedMode;
      if (this.dateInput && savedDate) this.dateInput.value = savedDate;
    }

    savePlanningContext() {
      const zip = this.cleanZip(this.zipInput?.value);
      const mode = this.planSelect?.value || 'now';
      const planDate = this.dateInput?.value || '';
      try {
        if (zip.length === 5) {
          localStorage.setItem(STORAGE_ZIP, zip);
          if (!localStorage.getItem(STORAGE_DEST)) localStorage.setItem(STORAGE_DEST, zip);
        }
        localStorage.setItem(STORAGE_PLAN_MODE, mode);
        if (planDate) localStorage.setItem(STORAGE_PLAN_DATE, planDate);
        else localStorage.removeItem(STORAGE_PLAN_DATE);
      } catch (error) {
        // Browser storage is optional.
      }
      window.AONSK_GARDEN_DATA?.updateProfilePreferences?.('seedFinder', { zip, mode, planDate }, {
        sourceName: 'Guided seed finder',
        sourceVersion: '10.22'
      });
    }

    cleanZip(value) {
      return String(value || '').replace(/\D/g, '').slice(0, 5);
    }

    combinedNaturalNote() {
      const parts = [];
      const written = String(this.note?.value || '').trim();
      if (written) parts.push(written);
      this.querySelectorAll('[data-finder-quick][aria-pressed="true"]').forEach((button) => {
        const phrase = String(button.dataset.finderQuickText || '').trim();
        if (phrase) parts.push(phrase);
      });
      return parts.join(', ');
    }

    prepareAndOpen() {
      this.applyNaturalNote(this.combinedNaturalNote());
      if (!this.validateSeasonStep()) {
        this.focusPlanning();
        return;
      }
      this.updateDialogContext();
      this.open();
    }

    focusPlanning() {
      this.close();
      const target = this.noteForm || this;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      requestAnimationFrame(() => {
        const zip = this.cleanZip(this.zipInput?.value);
        const mode = this.planSelect?.value || 'now';
        if (zip && zip.length !== 5) this.zipInput?.focus();
        else if (mode === 'later' && !dateFromInput(this.dateInput?.value)) this.dateInput?.focus();
        else this.zipInput?.focus();
      });
    }

    updateDialogContext() {
      if (!this.dialogContext) return;
      const zip = this.cleanZip(this.zipInput?.value);
      const mode = this.planSelect?.value || 'now';
      let timing = 'broad planting guidance';
      if (mode === 'now') timing = 'planting now';
      else if (mode === 'later') {
        const selectedDate = dateFromInput(this.dateInput?.value);
        timing = selectedDate ? `planting ${formatDate(selectedDate, { year: true })}` : 'another planting date';
      }
      else if (mode === 'next-year') timing = 'planning for next year';
      else if (mode === 'all-area') timing = 'viewing the full season';
      this.dialogContext.textContent = zip ? `Using ZIP ${zip} · ${timing}` : `No ZIP entered · ${timing}`;
    }

    open() {
      if (!this.dialog) return;
      this.currentStep = Math.min(this.currentStep, Math.max(0, this.steps.length - 1));
      this.updateStep();
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.documentElement.classList.add('aonsk-finder-is-open');
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else this.dialog.removeAttribute('open');
      document.documentElement.classList.remove('aonsk-finder-is-open');
    }

    advance() {
      const step = this.steps[this.currentStep];
      if (this.currentStep >= this.steps.length - 1) {
        this.finish();
        return;
      }
      this.currentStep += 1;
      this.updateStep();
    }

    skipCurrent() {
      const step = this.steps[this.currentStep];
      if (!step) {
        this.advance();
        return;
      }

      const stepName = step.dataset.finderStep;
      if (stepName === 'availability') {
        const showAll = step.querySelector('[data-availability="all"]');
        if (showAll) this.checkElement(showAll);
      } else {
        step.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = false;
        });
        step.querySelectorAll('.aonsk-finder__choice').forEach((choice) => {
          choice.classList.remove('is-selected');
        });
      }
      this.advance();
    }

    updateStep() {
      if (!this.steps.length) return;
      this.steps.forEach((step, index) => step.classList.toggle('is-active', index === this.currentStep));
      const isLast = this.currentStep === this.steps.length - 1;
      if (this.backButton) this.backButton.hidden = this.currentStep === 0;
      if (this.nextButton) this.nextButton.hidden = isLast;
      if (this.finishButton) this.finishButton.hidden = !isLast;
      if (this.skipButton) {
        const stepName = this.steps[this.currentStep]?.dataset.finderStep;
        if (isLast) this.skipButton.textContent = 'Keep all availability';
        else this.skipButton.textContent = 'Skip this question';
      }
      const progress = ((this.currentStep + 1) / this.steps.length) * 100;
      if (this.progressBar) this.progressBar.style.width = `${progress}%`;
      if (this.stepCount) this.stepCount.textContent = `Question ${this.currentStep + 1} of ${this.steps.length}`;
      const activeStep = this.steps[this.currentStep];
      const focusTarget = activeStep?.querySelector('input:not([type="hidden"]), textarea, button');
      focusTarget?.focus({ preventScroll: true });
    }

    selectedInput(stepName) {
      return this.querySelector(`[data-finder-step="${stepName}"] input[type="radio"]:checked`);
    }

    updateDateVisibility() {
      const mode = this.planSelect?.value || 'now';
      if (this.dateWrap) this.dateWrap.hidden = mode !== 'later';
      if (mode === 'later' && this.dateInput && !this.dateInput.value) {
        this.dateInput.value = toInputDate(addDays(new Date(), 30));
      }
    }

    validateSeasonStep() {
      const zip = this.cleanZip(this.zipInput?.value);
      const mode = this.planSelect?.value || 'now';
      if (zip && zip.length !== 5) {
        this.showSeasonError('Enter a five-digit US ZIP code, or clear the field to continue without a ZIP-based estimate.');
        this.zipInput?.focus();
        return false;
      }
      if (mode === 'later' && !dateFromInput(this.dateInput?.value)) {
        this.showSeasonError('Choose the planting date you would like the guide to check.');
        this.dateInput?.focus();
        return false;
      }
      this.clearSeasonError();
      this.savePlanningContext();
      return true;
    }

    showSeasonError(message) {
      if (!this.seasonError) return;
      this.seasonError.textContent = message;
      this.seasonError.hidden = false;
    }

    clearSeasonError() {
      if (!this.seasonError) return;
      this.seasonError.textContent = '';
      this.seasonError.hidden = true;
    }

    getFrostRecord(zip) {
      const clean = this.cleanZip(zip);
      if (clean.length !== 5) return null;
      return FROST[clean] || FROST[clean.slice(0, 3)] || null;
    }

    buildSeasonContext() {
      const zip = this.cleanZip(this.zipInput?.value);
      const mode = this.planSelect?.value || 'now';
      const record = this.getFrostRecord(zip);
      const today = startOfDay(new Date());
      let planDate = null;
      let year = today.getFullYear();

      if (mode === 'now') planDate = today;
      if (mode === 'later') {
        planDate = dateFromInput(this.dateInput?.value);
        if (planDate) year = planDate.getFullYear();
      }
      if (mode === 'next-year') year = today.getFullYear() + 1;

      if (!record) {
        return {
          zip,
          mode,
          record: null,
          today,
          planDate,
          year,
          phase: 'unknown',
          daysRemaining: null,
          seasonLength: null,
          lastFrost: null,
          firstFrost: null
        };
      }

      const lastFrost = dateFromMonthDay(record.last, year);
      const firstFrost = dateFromMonthDay(record.first, year);
      const allYear = record.last === '01-01' && record.first === '12-31';
      const seasonLength = Math.max(0, daysBetween(lastFrost, firstFrost));

      if (mode === 'next-year') planDate = addDays(lastFrost, 14);

      let phase = 'broad';
      let daysRemaining = null;
      if (allYear) {
        phase = 'all-year';
        if (planDate) daysRemaining = Math.max(0, daysBetween(planDate, firstFrost));
      } else if (mode === 'all-area' || mode === 'next-year') {
        phase = 'broad';
      } else if (planDate) {
        if (planDate < lastFrost) phase = 'before-season';
        else if (planDate > firstFrost) phase = 'after-season';
        else {
          phase = 'active';
          daysRemaining = Math.max(0, daysBetween(planDate, firstFrost));
        }
      }

      return {
        zip,
        mode,
        record,
        today,
        planDate,
        year,
        phase,
        allYear,
        daysRemaining,
        seasonLength,
        lastFrost,
        firstFrost
      };
    }

    updateSeasonPreview() {
      if (!this.seasonPreview) return;
      const zip = this.cleanZip(this.zipInput?.value);
      if (zip && zip.length !== 5) {
        this.seasonPreview.textContent = 'Keep entering the five-digit ZIP code. The seasonal estimate will appear here.';
        return;
      }

      const context = this.buildSeasonContext();
      if (!zip) {
        this.seasonPreview.textContent = 'Enter a ZIP code for an approximate seasonal check, or continue without one for broader recommendations.';
        return;
      }
      if (!context.record) {
        this.seasonPreview.textContent = 'This ZIP is not covered by the current regional lookup. You can still use every other part of the guide.';
        return;
      }

      const windowText = context.allYear
        ? 'The lookup treats this area as having little or no typical frost interruption.'
        : `The broad frost-free window is approximately ${formatDate(context.lastFrost)} through ${formatDate(context.firstFrost)}.`;

      let timingText = '';
      if (context.mode === 'next-year') {
        timingText = `Next year, the guide will keep the full season in view and use roughly ${formatDate(context.planDate, { year: true })} as a warm-season planning point.`;
      } else if (context.mode === 'all-area') {
        timingText = 'The guide will compare varieties with the area’s full typical season instead of narrowing them to one date.';
      } else if (context.phase === 'active') {
        timingText = `${formatDate(context.planDate, { year: true })} leaves about ${context.daysRemaining} frost-free days before the average first fall frost.`;
      } else if (context.phase === 'before-season') {
        timingText = `${formatDate(context.planDate, { year: true })} is before the estimated average last spring frost, so warm-season basil may be better started later or protected.`;
      } else if (context.phase === 'after-season') {
        timingText = `${formatDate(context.planDate, { year: true })} is after the estimated average first fall frost, so the guide will also surface options to save for next season.`;
      } else if (context.phase === 'all-year') {
        timingText = 'The selected date falls within the broad year-round window used by this lookup.';
      }

      this.seasonPreview.innerHTML = `<strong>ZIP ${context.zip}:</strong> ${windowText} ${timingText}`;
    }

    getState() {
      const intentInput = this.selectedInput('intent');
      const locationInput = this.selectedInput('location');
      const maturityInput = this.selectedInput('maturity');
      const availabilityInput = this.selectedInput('availability');
      const intent = this.config.intents?.find((item) => item.id === intentInput?.value) || null;

      return {
        intent,
        location: locationInput ? {
          value: locationInput.value,
          label: locationInput.dataset.filterLabel || '',
          param: locationInput.dataset.filterParam || ''
        } : null,
        maturity: maturityInput ? {
          value: maturityInput.value,
          label: maturityInput.dataset.filterLabel || '',
          param: maturityInput.dataset.filterParam || ''
        } : null,
        availability: availabilityInput ? {
          value: availabilityInput.value,
          label: availabilityInput.dataset.filterLabel || '',
          param: availabilityInput.dataset.filterParam || '',
          mode: availabilityInput.dataset.availability || 'all'
        } : { mode: 'all' },
        season: this.buildSeasonContext()
      };
    }

    applyNaturalNote(rawNote) {
      const note = normalizeText(rawNote).trim();
      if (!note) return;

      let bestIntent = null;
      let bestIntentScore = 0;
      (this.config.intents || []).forEach((intent) => {
        const keywords = (intent.keywords || []).map((keyword) => normalizeText(keyword).trim()).filter(Boolean);
        let score = 0;
        keywords.forEach((keyword) => {
          if (note.includes(keyword)) score += keyword.length > 7 ? 3 : 2;
        });
        if (intent.specialMode === 'container' && /container|pot|porch|patio|balcony|small space/.test(note)) score += 2;
        if (score > bestIntentScore) {
          bestIntentScore = score;
          bestIntent = intent;
        }
      });
      if (bestIntent) this.checkRadio(`input[name^="finder-intent-"][value="${CSS.escape(bestIntent.id)}"]`);

      const locationInputs = Array.from(this.querySelectorAll('[data-finder-step="location"] input'));
      const locationSynonyms = [
        { pattern: /container|pot|porch|patio|balcony/, values: ['container'] },
        { pattern: /raised bed/, values: ['raised bed'] },
        { pattern: /in[ -]?ground|field|garden bed/, values: ['in-ground'] },
        { pattern: /greenhouse/, values: ['greenhouse'] },
        { pattern: /high tunnel/, values: ['high tunnel'] },
        { pattern: /hydroponic/, values: ['hydroponic'] },
        { pattern: /indoor|windowsill|inside/, values: ['indoor'] },
        { pattern: /vertical|tower/, values: ['vertical'] },
        { pattern: /community garden/, values: ['community garden'] }
      ];
      for (const group of locationSynonyms) {
        if (!group.pattern.test(note)) continue;
        const input = locationInputs.find((candidate) => {
          const label = normalizeText(candidate.dataset.filterLabel);
          return group.values.some((value) => label.includes(value));
        });
        if (input) {
          this.checkElement(input);
          break;
        }
      }

      const zipMatch = note.match(/\b(\d{5})(?:-\d{4})?\b/);
      if (zipMatch && this.zipInput) this.zipInput.value = zipMatch[1];

      if (this.planSelect) {
        if (/next year|next season|for spring next/.test(note)) this.planSelect.value = 'next-year';
        else if (/all options|all varieties|any time|not planting yet|just exploring/.test(note)) this.planSelect.value = 'all-area';
        else if (/plant(?:ing)? now|right now|this week|today|still plant/.test(note)) this.planSelect.value = 'now';
      }

      const maturityInputs = Array.from(this.querySelectorAll('[data-finder-step="maturity"] input'));
      if (/fast|quick|soon|early|short season/.test(note) && maturityInputs.length) {
        const sorted = maturityInputs.slice().sort((a, b) => {
          const aRange = parseRange(a.dataset.filterLabel);
          const bRange = parseRange(b.dataset.filterLabel);
          return (aRange.min || 9999) - (bRange.min || 9999);
        });
        this.checkElement(sorted[0]);
      } else {
        const dayMatch = note.match(/(?:about|under|within|around)?\s*(\d{2,3})\s*days?/);
        if (dayMatch) {
          const desired = Number(dayMatch[1]);
          const input = maturityInputs.find((candidate) => {
            const range = parseRange(candidate.dataset.filterLabel);
            return range.min !== null && desired >= range.min && desired <= range.max;
          });
          if (input) this.checkElement(input);
        }
      }

      if (/in stock|available now|ship now|order now/.test(note)) {
        const inStock = this.querySelector('[data-availability="in-stock"]');
        if (inStock) this.checkElement(inStock);
      }

      this.savePlanningContext();
      this.updateDateVisibility();
      this.updateSeasonPreview();
      this.updateDialogContext();
    }

    syncChoiceStates() {
      this.querySelectorAll('.aonsk-finder__choice').forEach((choice) => {
        choice.classList.toggle('is-selected', Boolean(choice.querySelector('input')?.checked));
      });
    }

    checkRadio(selector) {
      const input = this.querySelector(selector);
      if (input) this.checkElement(input);
    }

    checkElement(input) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    productText(product) {
      return normalizeText([
        product.title,
        product.tags,
        product.type,
        product.locations,
        product.uses,
        product.flavor,
        product.growthHabit,
        product.sun,
        product.highlight,
        product.benefits,
        product.description
      ]);
    }

    productMaturity(product) {
      const textRange = parseRange(product.maturityText);
      const min = numberFrom(product.maturityMin) ?? textRange.min;
      const max = numberFrom(product.maturityMax) ?? textRange.max ?? min;
      return { min, max };
    }

    assessSeasonFit(product, season) {
      const maturity = this.productMaturity(product);
      const neutral = {
        score: 0,
        exact: true,
        group: 'primary',
        label: '',
        className: '',
        reason: ''
      };

      if (!season?.record) return neutral;

      const seasonLength = season.seasonLength;
      const matureWithinFullSeason = maturity.min === null || seasonLength === null || maturity.min + 7 <= seasonLength;

      if (season.mode === 'all-area') {
        return {
          score: matureWithinFullSeason ? 3 : -1,
          exact: matureWithinFullSeason,
          group: 'primary',
          label: 'Area-wide season view',
          className: 'is-broad',
          reason: matureWithinFullSeason
            ? 'Fits within the area’s broad typical frost-free season'
            : 'May need an especially early start or protected growing in this area'
        };
      }

      if (season.mode === 'next-year') {
        return {
          score: matureWithinFullSeason ? 4 : 0,
          exact: matureWithinFullSeason,
          group: 'primary',
          label: 'Next-season plan',
          className: 'is-next',
          reason: matureWithinFullSeason
            ? `Can be considered for next year’s typical season after about ${formatDate(season.lastFrost)}`
            : 'May need an earlier indoor start or protected conditions next year'
        };
      }

      if (season.phase === 'all-year') {
        return {
          score: 5,
          exact: true,
          group: 'primary',
          label: 'Season window fits',
          className: 'is-strong',
          reason: 'The selected date fits the broad year-round window used for this ZIP'
        };
      }

      if (season.phase === 'before-season') {
        return {
          score: -4,
          exact: false,
          group: 'later',
          label: 'Better after frost',
          className: 'is-later',
          reason: `Warm-season basil is generally better after the estimated last frost around ${formatDate(season.lastFrost)}`
        };
      }

      if (season.phase === 'after-season') {
        return {
          score: -6,
          exact: false,
          group: 'later',
          label: 'Save for next season',
          className: 'is-later',
          reason: 'The selected date falls after the broad average first-frost window'
        };
      }

      if (season.phase === 'active') {
        if (maturity.min === null) {
          return {
            score: 0,
            exact: false,
            group: 'primary',
            label: 'Check maturity details',
            className: 'is-possible',
            reason: `${season.daysRemaining} estimated frost-free days remain, but this product needs clearer maturity data`
          };
        }

        const conservativeDays = (maturity.max ?? maturity.min) + 7;
        const minimumDays = maturity.min + 7;
        if (conservativeDays <= season.daysRemaining) {
          return {
            score: 8,
            exact: true,
            group: 'primary',
            label: 'Good planting-window fit',
            className: 'is-strong',
            reason: `Its listed maturity fits comfortably within about ${season.daysRemaining} frost-free days`
          };
        }
        if (minimumDays <= season.daysRemaining) {
          return {
            score: 3,
            exact: false,
            group: 'primary',
            label: 'Possible this season',
            className: 'is-possible',
            reason: `The earlier end of its maturity range may fit within about ${season.daysRemaining} frost-free days`
          };
        }
        return {
          score: -5,
          exact: false,
          group: 'later',
          label: 'Better for another window',
          className: 'is-later',
          reason: `Its listed maturity is longer than the roughly ${season.daysRemaining} frost-free days remaining`
        };
      }

      return neutral;
    }

    scoreProduct(product, state) {
      const text = this.productText(product);
      const reasons = [];
      let score = product.available ? 1 : 0;
      let exact = true;

      if (state.availability?.mode === 'in-stock' && !product.available) {
        return { product, score: -999, reasons, exact: false, excluded: true, season: null };
      }

      if (state.intent) {
        if (state.intent.specialMode === 'surprise') {
          score += (hashNumber(product.id) % 1000) / 1000;
          reasons.push('A less predictable pick from this collection');
        } else {
          let intentMatches = 0;
          (state.intent.keywords || []).forEach((rawKeyword) => {
            const keyword = normalizeText(rawKeyword).trim();
            if (keyword && text.includes(keyword)) intentMatches += 1;
          });
          if (intentMatches > 0) {
            score += Math.min(10, intentMatches * 2.5);
            reasons.push(`Fits your “${state.intent.label}” direction`);
          } else {
            exact = false;
          }

          if (state.intent.specialMode === 'container') {
            const locationText = normalizeText(product.locations);
            const containerFit = product.containerSuitable === true || locationText.includes('container');
            if (containerFit) {
              score += 8;
              reasons.push('Suited to container growing');
            } else {
              score -= 3;
              exact = false;
            }
          }
        }
      }

      if (state.location) {
        const selected = normalizeText(state.location.label);
        const locations = normalizeText(product.locations);
        const normalizedSelected = selected.replace(/s$/, '');
        if (locations.includes(selected) || locations.includes(normalizedSelected)) {
          score += 7;
          reasons.push(`Listed for ${state.location.label.toLowerCase()}`);
        } else if (selected.includes('container') && product.containerSuitable === true) {
          score += 7;
          reasons.push('Marked container suitable');
        } else {
          score -= 2;
          exact = false;
        }
      }

      if (state.maturity) {
        const requested = parseRange(state.maturity.label);
        const productRange = this.productMaturity(product);
        if (requested.min !== null && productRange.min !== null) {
          const productMax = productRange.max ?? productRange.min;
          const overlaps = productRange.min <= requested.max && productMax >= requested.min;
          if (overlaps) {
            score += 7;
            reasons.push(`Maturity aligns with ${state.maturity.label.toLowerCase()}`);
          } else {
            score -= 2;
            exact = false;
          }
        } else {
          exact = false;
        }
      }

      const seasonAssessment = this.assessSeasonFit(product, state.season);
      score += seasonAssessment.score;
      if (seasonAssessment.reason) reasons.push(seasonAssessment.reason);
      if (!seasonAssessment.exact) exact = false;

      if (product.available && state.availability?.mode === 'in-stock') {
        score += 3;
        reasons.push('Available now');
      }

      if (!reasons.length) reasons.push('A useful place to begin exploring this collection');
      return {
        product,
        score,
        reasons: reasons.slice(0, 4),
        exact,
        excluded: false,
        season: seasonAssessment
      };
    }

    finish() {
      if (!this.validateSeasonStep()) {
        this.focusPlanning();
        return;
      }
      const state = this.getState();
      const scored = (this.config.products || [])
        .map((product) => this.scoreProduct(product, state))
        .filter((item) => !item.excluded)
        .sort((a, b) => {
          if (state.intent?.specialMode === 'surprise') return b.score - a.score;
          if (b.score !== a.score) return b.score - a.score;
          if (a.product.available !== b.product.available) return a.product.available ? -1 : 1;
          return String(a.product.title).localeCompare(String(b.product.title));
        });

      const primaryPool = scored.filter((item) => item.season?.group !== 'later');
      const laterPool = scored.filter((item) => item.season?.group === 'later');
      const exactMatches = primaryPool.filter((item) => item.exact && item.score > 0);
      const max = Math.max(1, Number(this.config.maxRecommendations) || 6);
      const recommendations = (exactMatches.length >= 3 ? exactMatches : primaryPool.length ? primaryPool : scored).slice(0, max);
      const laterRecommendations = (state.season?.mode === 'now' || state.season?.mode === 'later')
        ? laterPool.slice(0, 3)
        : [];

      this.renderResults(recommendations, laterRecommendations, state, exactMatches.length);
      this.close();
      if (this.results) {
        this.results.hidden = false;
        requestAnimationFrame(() => {
          this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
          this.results.focus({ preventScroll: true });
        });
      }
    }

    renderResults(recommendations, laterRecommendations, state, exactCount) {
      if (!this.resultGrid) return;
      this.resultGrid.replaceChildren();
      if (this.laterGrid) this.laterGrid.replaceChildren();

      if (!recommendations.length) {
        const empty = document.createElement('p');
        empty.className = 'aonsk-finder__empty';
        empty.textContent = 'We do not have enough product information to make a confident shortlist yet. The full collection and regular filters are still available below.';
        this.resultGrid.append(empty);
      } else {
        recommendations.forEach((item, index) => this.resultGrid.append(this.createCard(item, index)));
      }

      if (this.laterWrap) this.laterWrap.hidden = !laterRecommendations.length;
      if (this.laterGrid && laterRecommendations.length) {
        laterRecommendations.forEach((item, index) => this.laterGrid.append(this.createCard(item, index + recommendations.length, true)));
      }

      const selections = [];
      if (state.intent?.label) selections.push(state.intent.label.toLowerCase());
      if (state.location?.label) selections.push(state.location.label.toLowerCase());
      if (state.maturity?.label) selections.push(state.maturity.label.toLowerCase());
      if (state.availability?.mode === 'in-stock') selections.push('currently available seeds');

      if (this.resultsHeading) {
        this.resultsHeading.textContent = state.intent?.specialMode === 'surprise'
          ? 'Here are a few seeds worth discovering.'
          : 'Here are a few seeds we would look at first.';
      }

      if (this.resultsSummary) {
        const seasonReminder = state.season?.record && (state.season.mode === 'now' || state.season.mode === 'later')
          ? ' This shortlist reflects the selected planting window, not every basil that can grow in your area.'
          : ' The complete collection remains available below.';
        if (!selections.length) {
          this.resultsSummary.textContent = `We kept the search broad and selected a few useful starting points from ${this.config.collectionTitle}.${seasonReminder}`;
        } else if (exactCount >= 3) {
          this.resultsSummary.textContent = `These recommendations best reflect ${this.joinWords(selections)}.${seasonReminder}`;
        } else {
          this.resultsSummary.textContent = `Only a few products matched every detail, so we also included the closest useful options for ${this.joinWords(selections)}.${seasonReminder}`;
        }
      }

      this.renderSeasonResult(state.season);

      if (this.browseLink) {
        const url = this.buildFilterUrl(state);
        this.browseLink.href = url;
        const filterCount = [state.location, state.maturity, state.availability?.mode === 'in-stock' ? state.availability : null].filter(Boolean).length;
        this.browseLink.textContent = filterCount > 0 ? 'Open the collection with these filters' : `Browse all ${this.config.collectionTitle}`;
      }
    }

    renderSeasonResult(season) {
      if (!this.seasonResult) return;
      if (!season?.record) {
        this.seasonResult.hidden = true;
        this.seasonResult.textContent = '';
        return;
      }

      let heading = 'Seasonal planning view';
      let text = '';
      if (season.mode === 'next-year') {
        heading = 'Planning for next year';
        text = `For ZIP ${season.zip}, the broad frost-free window is approximately ${formatDate(season.lastFrost)} through ${formatDate(season.firstFrost)}. Current-season timing did not narrow your results.`;
      } else if (season.mode === 'all-area') {
        heading = 'Full-season view for your area';
        text = `For ZIP ${season.zip}, the guide compared listed maturity with the broad season from about ${formatDate(season.lastFrost)} through ${formatDate(season.firstFrost)}, without limiting results to one date.`;
      } else if (season.phase === 'active') {
        heading = 'What may still fit this planting window';
        text = `For ZIP ${season.zip}, the selected date leaves roughly ${season.daysRemaining} days before the broad average first-frost estimate of ${formatDate(season.firstFrost)}.`;
      } else if (season.phase === 'before-season') {
        heading = 'The selected date is early for warm-season basil';
        text = `For ZIP ${season.zip}, the broad average last-frost estimate is around ${formatDate(season.lastFrost)}. Results include varieties to consider after that point.`;
      } else if (season.phase === 'after-season') {
        heading = 'The selected date is beyond the broad frost-free window';
        text = `For ZIP ${season.zip}, the broad average first-frost estimate is around ${formatDate(season.firstFrost)}. Results also show varieties worth saving for next season.`;
      } else {
        text = `For ZIP ${season.zip}, the guide used an approximate frost window to support—not replace—your own local judgment.`;
      }

      this.seasonResult.hidden = false;
      this.seasonResult.innerHTML = `<strong>${heading}</strong><span>${text} This is not live weather, and it is not the only set of seeds that can grow in your area.</span>`;
    }

    createCard(item, index, compact = false) {
      const product = item.product;
      const article = document.createElement('article');
      article.className = `aonsk-finder__result-card${compact ? ' is-compact' : ''}`;

      const mediaLink = document.createElement('a');
      mediaLink.className = 'aonsk-finder__result-media';
      mediaLink.href = product.url;
      mediaLink.setAttribute('aria-label', product.title);
      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = product.imageAlt || product.title;
        image.loading = index < 2 ? 'eager' : 'lazy';
        image.width = 720;
        image.height = 540;
        mediaLink.append(image);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'aonsk-finder__result-fallback';
        fallback.textContent = 'Seed';
        mediaLink.append(fallback);
      }

      const status = document.createElement('span');
      status.className = `aonsk-finder__status ${product.available ? 'is-available' : 'is-sold-out'}`;
      status.textContent = product.available ? 'Available now' : 'Currently sold out';
      mediaLink.append(status);

      if (item.season?.label) {
        const seasonTag = document.createElement('span');
        seasonTag.className = `aonsk-finder__season-tag ${item.season.className || ''}`;
        seasonTag.textContent = item.season.label;
        mediaLink.append(seasonTag);
      }

      const body = document.createElement('div');
      body.className = 'aonsk-finder__result-body';

      const reasonLabel = document.createElement('p');
      reasonLabel.className = 'aonsk-finder__result-kicker';
      reasonLabel.textContent = 'Why it may fit';

      const heading = document.createElement('h4');
      const titleLink = document.createElement('a');
      titleLink.href = product.url;
      titleLink.textContent = product.title;
      heading.append(titleLink);

      const reasonList = document.createElement('ul');
      item.reasons.forEach((reason) => {
        const listItem = document.createElement('li');
        listItem.textContent = reason;
        reasonList.append(listItem);
      });

      const footer = document.createElement('div');
      footer.className = 'aonsk-finder__result-footer';
      const price = document.createElement('strong');
      price.textContent = product.price;
      const view = document.createElement('a');
      view.href = product.url;
      view.textContent = 'View seed details';
      footer.append(price, view);

      body.append(reasonLabel, heading, reasonList, footer);
      article.append(mediaLink, body);
      return article;
    }

    buildFilterUrl(state) {
      const browserOrigin = window.location.origin && window.location.origin !== 'null'
        ? window.location.origin
        : 'https://aonsk-seed-finder.invalid';
      const url = new URL(this.config.collectionUrl || window.location.pathname, browserOrigin);
      const addFilter = (filter) => {
        if (!filter?.param || filter.value === 'all') return;
        url.searchParams.append(filter.param, filter.value);
      };
      addFilter(state.location);
      addFilter(state.maturity);
      if (state.availability?.mode === 'in-stock') addFilter(state.availability);
      url.hash = 'filter-results';
      if (url.hostname === 'aonsk-seed-finder.invalid') return `${url.pathname}${url.search}${url.hash}`;
      return url.toString();
    }

    joinWords(items) {
      if (items.length <= 1) return items[0] || '';
      if (items.length === 2) return `${items[0]} and ${items[1]}`;
      return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
    }

    scrollToProducts() {
      const target = document.querySelector(this.config.productsAnchor || '#filter-results');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    reset() {
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = input.dataset.availability === 'all' || input.dataset.planMode === 'now';
      });
      this.syncChoiceStates();
      if (this.note) this.note.value = '';
      if (this.planSelect) this.planSelect.value = 'now';
      if (this.dateInput) this.dateInput.value = '';
      this.querySelectorAll('[data-finder-quick]').forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.classList.remove('is-selected');
      });
      this.currentStep = 0;
      this.updateDateVisibility();
      this.updateSeasonPreview();
      this.updateStep();
      if (this.results) this.results.hidden = true;
      this.open();
    }
  }

  if (!customElements.get('aonsk-seed-finder')) {
    customElements.define('aonsk-seed-finder', AonskSeedFinder);
  }
})();
